import type { ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import { PAYMENT_METHOD_CREDIT_CARD_STATUS_ENUM } from '@woovi/shared';

import type { PaymentActive_charge$key } from './__generated__/PaymentActive_charge.graphql.ts';
import type { PaymentActive_query$key } from './__generated__/PaymentActive_query.graphql.ts';
import PaymentActiveCreditCard from './creditCard/PaymentActiveCreditCard.tsx';
import PaymentActiveCreditCardFailed from './creditCard/PaymentActiveCreditCardFailed.tsx';
import PaymentActiveInstallment from './installment/PaymentActiveInstallment.tsx';
import PaymentActiveITP from './itp/PaymentActiveITP.tsx';
import PaymentActiveITPBanks from './itp/PaymentActiveITPBanks.tsx';
import PaymentActiveCob from './PaymentActiveCob.tsx';
import PaymentActivePixCreditCard from './pixCreditCard/PaymentActivePixCreditCard.tsx';
import { useChargePaymentMethod } from '../../charge/useChargePaymentMethod.tsx';
import usePaymentContext from '../context/usePaymentContext.tsx';

type PaymentActiveProps = {
  query: PaymentActive_query$key;
  charge: PaymentActive_charge$key;
  alwaysShowQrCode?: boolean;
  allowSimulatePayment?: boolean;
};

const PaymentActive = (props: PaymentActiveProps): ReactNode => {
  const query = useFragment<PaymentActive_query$key>(
    graphql`
      fragment PaymentActive_query on Query {
        ...PaymentActivePixCreditCard_query
        ...PaymentActiveInstallment_query
      }
    `,
    props.query,
  );

  const charge = useFragment<PaymentActive_charge$key>(
    graphql`
      fragment PaymentActive_charge on Charge {
        id
        status
        ...useChargePaymentMethod_charge
        ...PaymentActivePixCreditCard_charge
        ...PaymentActiveInstallment_charge
        ...PaymentActiveCob_charge
        ...PaymentActiveCreditCard_charge
        ...PaymentActiveCreditCardFailed_charge
        ...PaymentActiveITPBanks_charge
        ...PaymentActiveITP_charge
      }
    `,
    props.charge,
  );

  const { isITP, isCreditCard, creditCard, isInstallments, isPixCreditCard } =
    useChargePaymentMethod(charge);

  const { renderITPBanks } = usePaymentContext();

  if (renderITPBanks) {
    return <PaymentActiveITPBanks charge={charge} />;
  }

  if (isITP) {
    return <PaymentActiveITP charge={charge} />;
  }

  if (isPixCreditCard) {
    return <PaymentActivePixCreditCard charge={charge} query={query} />;
  }

  if (isCreditCard) {
    if (creditCard?.status === PAYMENT_METHOD_CREDIT_CARD_STATUS_ENUM.FAILED) {
      return <PaymentActiveCreditCardFailed charge={charge} />;
    }

    // active
    return <PaymentActiveCreditCard charge={charge} />;
  }

  if (isInstallments) {
    return <PaymentActiveInstallment query={query} charge={charge} />;
  }

  return (
    <PaymentActiveCob
      alwaysShowQrCode={props.alwaysShowQrCode}
      allowSimulatePayment={props.allowSimulatePayment}
      charge={charge}
    />
  );
};

export default PaymentActive;
